/**
 * @prettier
 */
import { useEffect, useState } from "react";
import { navigate } from "@reach/router";
import { auth } from "firebase-tools/firebase";
import { useUser } from "UserContext";
import { LocationType } from "types/PageType";
import { UserService } from "services/UserService";

export type AuthHookType = {
  location?: LocationType;
};

enum fallbackUrl {
  APP = "/",
  SIGNIN = "/sign-in",
  USERMANAGEMENT = "/admin-panel",
  SIGNUP = "/sign-up",
  FORGOTPASS = "/forgot-password",
}

// The staff record last loaded, by Auth uid. The hook re-subscribes on every page change and
// re-reads the record each time; a slow read on a busy page (Healthcheck) used to sign the person
// out mid-session (Carly R, 2026-11-04). Once loaded, a failed re-read keeps the session.
let loadedUser: { uid: string; user: any } | null = null;

export const useAuthHook = ({ location }: AuthHookType) => {
  const { pathname } = location as LocationType;
  const [, setUser] = useUser() as any;
  const [isAllowRender, setIsAllowRender] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(
      async (userAuth: any) => {
        if (userAuth) {
          const User = new UserService(userAuth.uid);

          // The user-doc read can hang indefinitely on a wedged Firestore
          // connection, which left the app on the loading bar forever.
          // Time it out so setIsAllowRender(true) below always runs.
          // Safari can also fail the read INSTANTLY (wedged IndexedDB
          // persistence marks the client offline while Auth stays healthy),
          // so retry before treating it as fatal.
          let user: any;
          let fetchFailed = false;
          for (let attempt = 0; attempt < 2; attempt++) {
            let timeoutId: any;
            try {
              user = await Promise.race([
                User.getUserByDoc(),
                new Promise((_, reject) => {
                  timeoutId = setTimeout(
                    () => reject(new Error("user fetch timed out")),
                    15000
                  );
                }),
              ]);
              fetchFailed = false;
              break;
            } catch (error) {
              fetchFailed = true;
              await new Promise((resolve) => setTimeout(resolve, 1000));
            } finally {
              clearTimeout(timeoutId);
            }
          }

          const loaded = loadedUser;
          if (fetchFailed && loaded && loaded.uid === userAuth.uid) {
            setUser(loaded.user);
            setIsAllowRender(true);
            return;
          }

          if (fetchFailed) {
            // Auth is valid but the user doc is unreadable. Rendering the
            // app in this state ping-pongs between PrivateRoute (empty user
            // -> /sign-in) and this hook (signed in -> /admin-panel) - the
            // Safari screen-flash loop. Sign out cleanly instead: the
            // listener re-fires with null and lands on /sign-in once.
            await signOut();
            setIsAllowRender(true);
            return;
          }

          if (!!!user?.email) {
            signOut();
          }

          if (user?.role === "user") {
            await signOut();
          }

          if (user?.email && user?.role !== "user")
            loadedUser = { uid: userAuth.uid, user };
          setUser(user);
        }
        setIsAllowRender(true);
        if (
          !userAuth &&
          pathname !== fallbackUrl.SIGNUP &&
          pathname !== fallbackUrl.FORGOTPASS
        ) {
          navigate(fallbackUrl.SIGNIN);
        } else if (
          userAuth &&
          userAuth.email &&
          (pathname === fallbackUrl.SIGNIN ||
            pathname === fallbackUrl.SIGNUP ||
            pathname === fallbackUrl.APP)
        ) {
          document.body.classList.remove("body-signin");
          navigate(fallbackUrl.USERMANAGEMENT);
        }
      },
      () => {
        // auth listener error — never strand the user on the loading bar
        setIsAllowRender(true);
      }
    );
    return unsubscribe;
  }, [pathname, setUser]);

  return isAllowRender;
};

export const signOut = async () => {
  loadedUser = null;
  await auth.signOut();
};
